import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { History, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUDIT_ADMIN_EMAIL = 'f.conti@crimpiantielettrici.it';

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// Pulsante "Cronologia modifiche" — visibile SOLO all'amministratore autorizzato.
export const AuditLogButton = ({ fileId, fileNome, compact = false }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  if ((user?.email || '').trim().toLowerCase() !== AUDIT_ADMIN_EMAIL) return null;

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const r = await axios.get(`${API}/audit-log/files`, { params: { file_id: fileId }, withCredentials: true });
      setLogs(r.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento cronologia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {compact ? (
        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" onClick={openDialog} title="Cronologia modifiche" data-testid={`audit-log-btn-${fileId}`}>
          <History className="h-3.5 w-3.5" />
        </Button>
      ) : (
        <Button variant="ghost" size="sm" className="text-slate-500" onClick={openDialog} data-testid={`audit-log-btn-${fileId}`}>
          <History className="h-4 w-4 mr-1" />Cronologia modifiche
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[820px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cronologia modifiche{fileNome ? ` — ${fileNome}` : ''}</DialogTitle>
            <DialogDescription>Storico immutabile delle operazioni sul file · visibile solo all'amministratore autorizzato</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center" data-testid="audit-log-empty">Nessuna modifica registrata per questo file.</p>
          ) : (
            <Table data-testid="audit-log-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase text-slate-500">Data e ora</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Utente</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Email</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Azione</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 text-center">Versione</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(l => (
                  <TableRow key={l.id}>
                    <TableCell className="whitespace-nowrap font-mono text-xs">{fmtDate(l.timestamp)}</TableCell>
                    <TableCell className="text-sm">{l.user_name || 'Sconosciuto'}</TableCell>
                    <TableCell className="text-xs text-slate-500">{l.user_email || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className={l.azione === 'eliminazione' ? 'bg-red-50 text-red-700 border-red-200' : l.azione === 'creazione' || l.azione === 'caricamento file' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-700'}>{l.azione}</Badge></TableCell>
                    <TableCell className="text-center text-sm font-semibold">{l.versione ?? '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[200px] truncate" title={l.note}>{l.note || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
